import type { OverviewItem } from '@kontor/shared'
import { useQuery } from '@tanstack/react-query'
import {
  Archive,
  BellRing,
  CircleCheck,
  EyeOff,
  HardDrive,
  PlugZap,
  RefreshCw,
  ServerOff,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react'
import { Link } from 'react-router'

import { EmptyState } from '@/components/EmptyState'
import { Panel } from '@/components/Panel'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ApiRequestError, fetchOverview } from '@/lib/api'
import { cn } from '@/lib/utils'

const ICONS: Record<OverviewItem['kind'], LucideIcon> = {
  alert: BellRing,
  connection: PlugZap,
  backup: Archive,
  disk: HardDrive,
}

function formatSince(iso: string | null): string {
  if (iso === null) return ''
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000)
  if (minutes < 1) return 'gerade eben'
  if (minutes < 60) return `seit ${String(minutes)} min`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `seit ${String(hours)} h`
  return `seit ${String(Math.round(hours / 24))} Tagen`
}

function Row({ item }: { item: OverviewItem }) {
  const Icon = ICONS[item.kind]

  // Die ganze Zeile ist der Link — bei einem Problem will man dorthin, nicht auf ein Wort zielen.
  return (
    <Link
      to={item.href}
      className="hover:bg-muted/60 border-border/60 flex items-start gap-3 border-b px-4 py-3 last:border-b-0"
    >
      <Icon
        className={cn(
          'mt-0.5 size-4 shrink-0',
          item.severity === 'error' ? 'text-destructive' : 'text-status-warn',
        )}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-sm font-medium">{item.title}</span>
          {item.serverName ? <Badge variant="outline">{item.serverName}</Badge> : null}
          <span className="text-muted-foreground text-xs">{formatSince(item.at)}</span>
        </div>
        <p className="text-muted-foreground truncate text-sm">{item.detail}</p>
      </div>
    </Link>
  )
}

/** Die Startseite: beantwortet „muss ich heute irgendwo ran?" und führt direkt dorthin. */
export function OverviewPage() {
  const overview = useQuery({
    queryKey: ['overview'],
    queryFn: fetchOverview,
    // Das Lagebild liest nur aus der Datenbank; einmal pro Minute kostet praktisch nichts.
    refetchInterval: 60_000,
  })

  if (overview.isPending) return <Skeleton className="h-64 w-full" />

  if (overview.isError) {
    return (
      <Alert variant="destructive">
        <TriangleAlert />
        <AlertTitle>Lagebild nicht abrufbar</AlertTitle>
        <AlertDescription>
          {overview.error instanceof ApiRequestError
            ? overview.error.message
            : 'Unerwarteter Fehler.'}
        </AlertDescription>
      </Alert>
    )
  }

  const { items, serverCount, connectedCount, idleCount } = overview.data
  const errors = items.filter((item) => item.severity === 'error').length

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Übersicht</h1>
          <p className="text-muted-foreground text-sm">
            {items.length === 0
              ? `${String(serverCount)} Server, nichts liegt an.`
              : `${items.length === 1 ? '1 offener Punkt' : `${String(items.length)} offene Punkte`}${
                  errors > 0 ? `, davon ${String(errors)} dringend` : ''
                } · ${String(idleCount)} von ${String(serverCount)} Servern unauffällig`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">
            {String(connectedCount)}/{String(serverCount)} verbunden
          </Badge>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void overview.refetch()}
            disabled={overview.isFetching}
          >
            <RefreshCw className={overview.isFetching ? 'animate-spin' : undefined} />
            Neu lesen
          </Button>
        </div>
      </div>

      {/*
       * Ohne Verbindung misst der Poller nichts (§5.8). Das gehört über die Liste und nicht
       * anstelle der Liste: ein nicht erreichbarer Server ist genau dann ein Befund.
       */}
      {serverCount > 0 && connectedCount === 0 ? (
        <Alert>
          <EyeOff />
          <AlertTitle>Es wird gerade nichts gemessen</AlertTitle>
          <AlertDescription className="space-y-2">
            <span className="block">
              Kontor ist mit keinem Server verbunden. Was unten steht, ist der letzte bekannte Stand
              — kein Bild von jetzt.
            </span>
            <Button asChild size="sm" variant="outline">
              <Link to="/server">Server verbinden</Link>
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      {serverCount === 0 ? (
        <EmptyState
          icon={ServerOff}
          title="Noch kein Server angelegt"
          description="Sobald ein Server eingetragen ist, steht hier, was an ihm gerade nicht stimmt — und ein Klick bringt dich hin."
          action={
            <Button asChild size="sm">
              <Link to="/server">Zu den Servern</Link>
            </Button>
          }
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon={CircleCheck}
          title="Alles ruhig"
          description="Keine offenen Alarme, keine gescheiterten Backups, keine vollen Platten, alle verbundenen Server erreichbar."
        />
      ) : (
        <Panel>
          {items.map((item) => (
            <Row key={item.id} item={item} />
          ))}
        </Panel>
      )}

      <p className="text-muted-foreground text-xs">
        Gelesen aus dem, was Kontor ohnehin schon gemessen hat — diese Seite fragt keinen Server
        extra ab. Zertifikate und offene Updates erscheinen hier, sobald du dafür eine Regel unter{' '}
        <Link to="/alarme" className="underline">
          Alarme
        </Link>{' '}
        angelegt hast.
      </p>
    </div>
  )
}
